import { useConfig } from "context/ConfigContext";
import { useQuery } from "hooks/useQuery";
import { useAccount } from "context/AccountContext";
import { useHistory } from "react-router-dom";

const useData = () => {
  const { setProfile, setUser } = useAccount();
  const history = useHistory();
  const config = useConfig();
  const query = useQuery();
  const { getCsrfToken } = useAccount();

  const redirectLogin = () => {
    setUser(null);
    setProfile(null);
    if (window.location.pathname.startsWith('/login')) {
      return;
    }
    history.push('/login?destination=' + window.location.pathname + window.location.search);
  }

  const redirectMaintenance = () => {
    history.push('/onderhoud');
  }

  const getData = async (path, queryString, isJson = true, redirectOnError = true) => {
    let search_params = query;
    if (queryString) {
      search_params = new URLSearchParams(queryString);
    }

    search_params.set('_format', 'json');

    const requestedPage = search_params.get('page');
    if (requestedPage) {
      search_params.set('page', requestedPage - 1);
    }

    const uri = `${ config.host }/${ path }?${ search_params.toString() }`;
    let response;
    try {
      response = await fetch(uri, { credentials: 'include' });
    } catch (exception) {
      console.log(exception);
    }

    let data = null;
    // eslint-disable-next-line default-case
    switch (response?.status) {
      case 200:
        if (isJson) {
          data = await response.json();
        } else {
          data = await response.text();
        }
        break;

      case 403:
        if (redirectOnError) {
          redirectLogin();
        }
        break;

      case 503:
        if (redirectOnError) {
          redirectMaintenance();
        }
        break;
    }

    return { data, response };
  }

  const postData = async (path, values, redirectOnError = true, method = 'POST') => {
    const headers = {
      'x-csrf-token': await getCsrfToken(),
      'Content-Type': 'application/json',
    };
    const params = {
      credentials: 'include',
      headers: headers,
    };
    if (values) {
      params.body = JSON.stringify(values);
    }
    params.method = method;

    let response;
    try {
      response = await fetch(`${ config.host }/${ path }?_format=json`, params);
    } catch (exception) {
      console.log(exception);
    }

    if (redirectOnError) {
      if (response?.status === 403) {
        redirectLogin();
      } else if (response?.status === 503) {
        redirectMaintenance();
      }
    }

    return response;
  }

  const patchData = async (id, bundle, values, redirectOnError = true) => {
    const prefix = 'rest/';
    const path = `${ prefix }${ bundle }/${ id }`;
    return postData(path, values, redirectOnError, 'PATCH');
  }

  const postFile = async (type, bundle, field, file) => {
    const fileData = await file.arrayBuffer();
    const path = `file/upload/${ type }/${ bundle }/${ field }`;
    const params = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/octet-stream',
        'Content-Disposition': `file; filename="${ encodeURIComponent(file.name) }"`,
        'X-CSRF-Token': await getCsrfToken(),
      },
      body: fileData,
    };

    return await fetch(`${ config.host }/${ path }?_format=json`, params);
  }

  const deleteData = async (path, redirectOnError = true) => {
    return postData(path, null, redirectOnError, 'DELETE');
  }

  return { getData, postData, patchData, deleteData, postFile }
}

export default useData;
